import { useState } from "react";
import { ColorFormat, HSLColor, HSVColor, RGBColor } from "../../types";
import {
  rgbaToHex8,
  rgbaToString,
  hslaToString,
  hsvaToString,
} from "../../helpers";

// Color Display Component
export const ColorDisplay: React.FC<{
  color: string;
  format: ColorFormat;
  rgb: RGBColor;
  hsl: HSLColor;
  hsv: HSVColor;
  opacity: number;
}> = ({ color, format, rgb, hsl, hsv, opacity }) => {
  const [copied, setCopied] = useState(false);

  const getDisplayValue = () => {
    // If opacity is 1 (fully opaque), return formats without alpha
    if (opacity === 1) {
      switch (format) {
        case "rgb":
          return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
        case "hsl":
          return `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
        case "hsv":
          return `hsv(${hsv.h}, ${hsv.s}%, ${hsv.v}%)`;
        case "hex":
        default:
          return color;
      }
    }

    // If opacity is less than 1, include alpha channel
    switch (format) {
      case "rgb":
        return rgbaToString({ ...rgb, a: opacity });
      case "hsl":
        return hslaToString(hsl, opacity);
      case "hsv":
        return hsvaToString(hsv, opacity);
      case "hex":
      default:
        return rgbaToHex8({ ...rgb, a: opacity });
    }
  };

  const handleCopy = async () => {
    const value = getDisplayValue();
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Create a checkerboard pattern for transparency background
  const checkerboardStyle = {
    backgroundImage: `
      linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
      linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
      linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
    `,
    backgroundSize: "10px 10px",
    backgroundPosition: "0 0, 0 5px, 5px -5px, -5px 0px",
  };

  return (
    <div className="mt-4 p-2 py-1 bg-gray-800 border border-gray-600 rounded flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="relative">
          <div
            className="w-7 h-7 rounded border-2 border-gray-600"
            style={checkerboardStyle}
          >
            <div
              className="w-full h-full"
              style={{
                backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`,
              }}
            />
          </div>
        </div>
        <code className="text-gray-200  text-[13px]">{getDisplayValue()}</code>
      </div>
      <button
        onClick={handleCopy}
        className="p-2 hover:bg-gray-700 rounded transition-colors duration-200 text-gray-400 hover:text-white text-[14px]"
      >
        {copied ? <span className="text-green-400">Copied!</span> : "Copy"}
      </button>
    </div>
  );
};

export default ColorDisplay;
